<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast" tag="div">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="getToastClasses(toast)"
        >
          <div class="toast-icon">
            <i v-if="toast.type === 'success'" class="fas fa-check"></i>
            <i v-else-if="toast.type === 'error'" class="fas fa-times"></i>
            <i v-else-if="toast.type === 'warning'" class="fas fa-exclamation-triangle"></i>
            <span v-else-if="toast.type === 'info'">ℹ</span>
          </div>
          
          <div class="toast-content">
            <div v-if="toast.title" class="toast-title">
              {{ toast.title }}
            </div>
            <div class="toast-message" style="white-space: pre-line;">
              {{ toast.message }}
            </div>
            
            <!-- Action Buttons -->
            <div v-if="toast.actions && toast.actions.length > 0" class="toast-actions">
              <a
                v-for="(action, index) in toast.actions"
                :key="index"
                :href="action.url"
                class="toast-action-btn"
                target="_blank"
                rel="noopener noreferrer"
                @click="action.onClick"
              >
                {{ action.label }}
              </a>
            </div>
          </div>
          
          <button
            v-if="toast.closable !== false"
            class="toast-close"
            aria-label="關閉通知"
            @click="removeToast(toast.id)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { toasts } from '@/composables/useToast'

export interface ToastAction {
  label: string
  url?: string
  onClick?: () => void
}

export interface ToastItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  duration?: number
  closable?: boolean
  actions?: ToastAction[]
}

const removeToast = (id: string) => {
  const index = toasts.value.findIndex(toast => toast.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

const getToastClasses = (toast: ToastItem) => {
  return [
    'toast',
    `toast--${toast.type}`
  ]
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: var(--sp-4);
  right: var(--sp-4);
  z-index: var(--z-tooltip);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  min-width: 320px;
  max-width: 480px;
  padding: var(--sp-4);
  background-color: var(--c-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  border-left: 4px solid;
  pointer-events: auto;
  position: relative;
  overflow: hidden;
}

.toast::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.toast--success {
  border-left-color: var(--c-success);
}

.toast--error {
  border-left-color: var(--c-danger);
}

.toast--warning {
  border-left-color: var(--c-warning);
}

.toast--info {
  border-left-color: var(--c-info);
}

.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

.toast--success .toast-icon {
  background-color: var(--c-success-bg);
  color: var(--c-success);
}

.toast--error .toast-icon {
  background-color: var(--c-danger-bg);
  color: var(--c-danger);
}

.toast--warning .toast-icon {
  background-color: var(--c-warning-bg);
  color: var(--c-warning);
}

.toast--info .toast-icon {
  background-color: var(--c-info-bg);
  color: var(--c-info);
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--c-gray-800);
  margin-bottom: var(--sp-1);
  line-height: var(--line-height-tight);
}

.toast-message {
  font-size: var(--font-size-sm);
  color: var(--c-gray-600);
  line-height: var(--line-height-normal);
  word-wrap: break-word;
}

.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: var(--c-gray-400);
  cursor: pointer;
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
  flex-shrink: 0;
  font-size: var(--font-size-xs);
}

.toast-close:hover {
  background-color: var(--c-gray-200);
  color: var(--c-gray-600);
}

/* Action Buttons */
.toast-actions {
  display: flex;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
  flex-wrap: wrap;
}

.toast-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-1) var(--sp-3);
  background-color: var(--c-primary);
  color: white;
  text-decoration: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.toast-action-btn:hover {
  background-color: var(--c-primary-600);
  transform: translateY(-1px);
}

.toast-action-btn:active {
  transform: translateY(0);
}

/* Transitions */
.toast-enter-active {
  transition: all var(--transition-base);
}

.toast-leave-active {
  transition: all var(--transition-base);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.toast-move {
  transition: transform var(--transition-base);
}

/* Responsive */
@media (max-width: 640px) {
  .toast-container {
    top: var(--sp-2);
    right: var(--sp-2);
    left: var(--sp-2);
  }
  
  .toast {
    min-width: auto;
    width: 100%;
  }
}
</style>




